// TODO(#69): Technical debt
// Currently, PubNub response values are not strictly typed.
// To improve type safety and maintainability, PubNub type definitions should be organized and addressed in the future.
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Chat } from '../Chat';
import { Text } from '../Text';
import { Loading } from '../Loading';
import { Icon } from '../Icon';
import {
  MOBILE_HEADER_ICON_SIZE,
  mobileHeaderIconButtonClass,
} from '../Header/styles';
import {
  getChannelDetail,
  TChannelResponseStatus,
  updateChannelResponseStatus,
} from '../../api/messages';
import { getInquiryChannelDetail } from '../../api/inquiryChat';
import { getQueryOrganizationId } from '../../configs/queryClient';
import { commonStrings, messageStrings } from '../../constants';
import { useOrganizationScopeOrNull } from '../OrganizationScope/context';
import { useOrganizationToast } from '../../hooks/useOrganizationToast';
import {
  patchChannelResponseStatus,
  snapshotChannelResponseStatus,
  restoreChannelResponseStatus,
} from '../../utils/channelResponseStatus';
import { useCurrentUser } from 'hooks';

type Props = {
  channelId: string;
  loadingChannel?: boolean;
  isInquiryChat?: boolean;
  onBack?: () => void;
  onMessageRecalled?: (channelId: string) => void;
};

export function Conversation({
  channelId,
  loadingChannel = false,
  isInquiryChat = false,
  onBack,
  onMessageRecalled,
}: Props) {
  const { data: currentUser } = useCurrentUser();
  // 本人uuidはcurrent-user APIの値のみを使う（?uuid= フォールバックは廃止）
  const uuid = currentUser?.uuid || '';

  const { data, error, isLoading } = useQuery({
    queryKey: ['channel-detail', isInquiryChat ? 'inquiry' : 'male', channelId],
    queryFn: () =>
      isInquiryChat
        ? getInquiryChannelDetail(channelId)
        : getChannelDetail(channelId),
    enabled: !!channelId,
    retry: 0,
  });

  const queryClient = useQueryClient();
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const { toastError } = useOrganizationToast();
  const responseStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: TChannelResponseStatus;
    }) => updateChannelResponseStatus(id, status, organizationId),
    onMutate: async ({ id, status }) => {
      // 戻る/進むで組織が変わった後に解決しても、別組織のキャッシュへ書かない
      const queryOrganizationId = getQueryOrganizationId();
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['management-channels'] }),
        queryClient.cancelQueries({ queryKey: ['channel-detail', 'male', id] }),
      ]);
      if (queryOrganizationId !== getQueryOrganizationId()) return undefined;
      const snapshot = snapshotChannelResponseStatus(queryClient, id);
      patchChannelResponseStatus(queryClient, id, status);
      return { snapshot, queryOrganizationId };
    },
    onSuccess: (result, { id, status }, context) => {
      if (context?.queryOrganizationId !== getQueryOrganizationId()) return;
      // A new user message may already have superseded this manual change.
      patchChannelResponseStatus(queryClient, id, result.responseStatus, status);
    },
    onError: (_error, { id, status }, context) => {
      if (context && context.queryOrganizationId === getQueryOrganizationId())
        restoreChannelResponseStatus(queryClient, id, status, context.snapshot);
      toastError(messageStrings.responseStatus.updateFailed);
    },
    retry: 0,
  });

  // Filter participants to exclude current user
  const participants = useMemo(() => {
    return data?.participants?.filter((item) => item?.uuid !== uuid) || [];
  }, [data?.participants, uuid]);

  const verifiedInquiryChannel = isInquiryChat && data?.kind === 'INQUIRY';

  // モバイルで読込中・エラー時にも一覧へ戻れるようにする（通常時の戻るは ChatHeader 側）
  const mobileBackBar = onBack ? (
    <div className="flex items-center border-b border-neutral-200 p-2 md:hidden">
      <button
        type="button"
        onClick={onBack}
        aria-label={commonStrings.actions.backToList}
        className={`flex ${mobileHeaderIconButtonClass} items-center justify-center rounded-full text-ink transition hover:bg-neutral-50`}
      >
        <Icon
          icon="chevronLeft"
          width={MOBILE_HEADER_ICON_SIZE}
          height={MOBILE_HEADER_ICON_SIZE}
        />
      </button>
    </div>
  ) : null;

  if (loadingChannel || isLoading) {
    return (
      <div className="h-full flex flex-col">
        {mobileBackBar}
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="h-full flex flex-col">
        {mobileBackBar}
        <div className="flex-1 flex items-center justify-center">
          <Text center bold>
            {commonStrings.somethingWentWrong}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <Chat
      key={channelId}
      channel={channelId}
      participants={participants}
      loading={loadingChannel || isLoading}
      enableAi={verifiedInquiryChannel}
      skipLineNotify={verifiedInquiryChannel}
      inquiryChatMode={verifiedInquiryChannel}
      onBack={onBack}
      onMessageRecalled={onMessageRecalled}
      responseStatus={data.responseStatus}
      onChangeResponseStatus={
        isInquiryChat
          ? undefined
          : (status) => responseStatusMutation.mutate({ id: channelId, status })
      }
      isUpdatingResponseStatus={responseStatusMutation.isPending}
    />
  );
}
